import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	SortingState,
	getSortedRowModel,
	Row,
	Column,
	AccessorKeyColumnDef,
} from '@tanstack/react-table'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Button, EllipsisText, IconButton, Tooltip } from '../../components/ui'
import {
	Error,
	FileDownloadOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	Replay,
	UnfoldMore,
} from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import { useUserDateFormat } from '../useUserDateFormat'
import { PlanInvoice } from '../../lib/api/types/billing.interfaces'
import { getCurrencySymbol } from '../../lib/utils/billing/formatter'
import { useInvoice, useReceipt, useRetry } from '../api/billingHooks'

export const PlanInvoiceStatuses: Record<number, string> = {
	0: 'Pending',
	1: 'Paid',
	2: 'Failed',
}

interface UseCreateBillingHistoryTableProps {
	data: PlanInvoice[]
	isNarrow?: boolean
}

export const useCreateBillingHistoryTable = ({
	data,
	isNarrow,
}: UseCreateBillingHistoryTableProps) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<SortingState>([])
	const [currentDownloadId, setCurrentDownloadId] = useState<string | null>(
		null
	)
	const [currentRetryId, setCurrentRetryId] = useState<string | null>(null)
	const actionItemIds = useRef<string[]>([])

	const { refetch: getReceipt, isFetching: isLoadingReceipt } = useReceipt(
		{
			id: currentDownloadId || '',
		},
		{ enabled: false }
	)
	const { refetch: getInvoice, isFetching: isLoadingInvoice } = useInvoice(
		{
			id: currentDownloadId || '',
		},
		{ enabled: false }
	)

	const { mutateAsync: subscribe, isPending: isRetrying } = useRetry()

	useEffect(() => {
		if (currentDownloadId) {
			const downloadItem = data.find((item) => item.id === currentDownloadId)
			if (downloadItem) {
				const downloadAndClear = async () => {
					try {
						let result
						if (downloadItem.status === 1) {
							result = await getReceipt()
						} else {
							result = await getInvoice()
						}

						// Process and download PDF
						if (result?.data?.data) {
							// Import the helper function
							const processAndDownloadPdf = (
								pdfData: ArrayBuffer | string,
								filename?: string
							) => {
								if (!pdfData) return

								console.log('Processing PDF data:', typeof pdfData)

								try {
									let blob: Blob

									if (pdfData instanceof ArrayBuffer) {
										// Handle ArrayBuffer (binary data)
										blob = new Blob([pdfData], { type: 'application/pdf' })
									} else if (typeof pdfData === 'string') {
										// Handle base64 or string data
										if (pdfData.startsWith('%PDF')) {
											// Raw PDF string
											blob = new Blob([pdfData], { type: 'application/pdf' })
										} else {
											// Base64 encoded PDF
											const binaryString = atob(pdfData)
											const bytes = new Uint8Array(binaryString.length)
											for (let i = 0; i < binaryString.length; i++) {
												bytes[i] = binaryString.charCodeAt(i)
											}
											blob = new Blob([bytes], { type: 'application/pdf' })
										}
									} else {
										console.error('Unsupported PDF data type:', typeof pdfData)
										return
									}

									console.log('Creating PDF blob...')
									const url = URL.createObjectURL(blob)
									console.log('Opening PDF URL:', url)

									// Create a temporary link and trigger download
									const link = document.createElement('a')
									link.href = url
									link.download = filename || `document-${Date.now()}.pdf`
									document.body.appendChild(link)
									link.click()
									document.body.removeChild(link)

									// Clean up the object URL after a delay
									setTimeout(() => URL.revokeObjectURL(url), 1000)
								} catch (error) {
									console.error('Error processing PDF:', error)
								}
							}

							const filename =
								downloadItem.status === 1
									? `receipt-${downloadItem.id}.pdf`
									: `invoice-${downloadItem.id}.pdf`

							processAndDownloadPdf(
								result.data.data as ArrayBuffer | string,
								filename
							)
						}
					} catch (error) {
						console.error('Error downloading PDF:', error)
					} finally {
						setCurrentDownloadId(null)
						actionItemIds.current = actionItemIds?.current?.filter(
							(aId) => aId !== currentDownloadId
						)
					}
				}
				downloadAndClear()
			}
		}
	}, [currentDownloadId, data, getReceipt, getInvoice])

	const handleDownload = async (id: string, status: number) => {
		actionItemIds?.current?.push(id)
		setCurrentDownloadId(id)
	}

	const handleRetry = async (id: string) => {
		setCurrentRetryId(id)
		await subscribe({ id }, { onSettled: () => setCurrentRetryId(null) })
	}

	const renderSortingHeader = (
		title: string,
		column: Column<PlanInvoice, unknown>,
		className?: string,
		leftIcon?: ReactNode
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border dark:text-text-table-dark',
					className
				)}
			>
				{leftIcon}
				<EllipsisText
					tooltipLabel={title}
					text={title}
					className="text-center w-fit max-w-auto truncate"
				/>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column.getIsSorted() === false && 'hidden group-hover:inline-block'
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const columns = useMemo<ColumnDef<PlanInvoice>[]>(() => {
		const base: ColumnDef<PlanInvoice>[] = [
			{
				accessorKey: 'date',
				enableSorting: true,
				sortingFn: 'datetime',
				enableResizing: true,
				size: isNarrow ? 60 : 150,
				minSize: 70,
				header: ({ column }) => renderSortingHeader('Date', column),
				cell: (info) => {
					const val = info.getValue()
					return (
						<Tooltip label={val ? formatDateTime(String(val)) : ''} classNameChildrenWrapper="w-fit">
							<span>{val ? formatDate(String(val)) : '-'}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'plan',
				enableSorting: true,
				sortingFn: 'alphanumeric',
				enableResizing: true,
				size: isNarrow ? 70 : 150,
				minSize: 70,
				header: ({ column }) => renderSortingHeader('Plan', column),
				cell: (info) => info.getValue(),
			},
			{
				accessorKey: 'amount',
				enableSorting: true,
				sortingFn: 'alphanumeric',
				enableResizing: true,
				size: isNarrow ? 50 : 150,
				minSize: 50,
				header: ({ column }) => renderSortingHeader('Amount', column),
				cell: (info) =>
					`${getCurrencySymbol(info.row.original.currency)}${(Number(info.getValue()) / 100).toFixed(2)}`,
			},
			{
				accessorKey: 'status',
				enableSorting: true,
				sortingFn: 'alphanumeric',
				enableResizing: true,
				size: isNarrow ? 60 : 150,
				minSize: 60,
				header: ({ column }) => renderSortingHeader('Status', column),
				cell: (info) => PlanInvoiceStatuses[Number(info.getValue())],
			},
			{
				accessorKey: 'id',
				header: () => <p className="text-start px-5">Invoice</p>,
				cell: ({ row }) => {
					return row?.original?.status === 1 ? (
						<Button
							variant="secondary"
							leftIcon={<FileDownloadOutlined className="w-5 h-5" />}
							label="Download PDF"
							loading={
								isLoadingReceipt && currentDownloadId === row?.original?.id
							}
							onClick={() =>
								handleDownload(row.original?.id, row?.original?.status)
							}
						/>
					) : (
						<div className="flex items-center justify-start gap-2">
							<IconButton
								variant="secondary"
								icon={<FileDownloadOutlined className="w-5 h-5" />}
								tooltip="Download invoice (PDF)"
								loading={
									isLoadingInvoice && currentDownloadId === row?.original?.id
								}
								onClick={() =>
									handleDownload(row.original?.id, row?.original?.status)
								}
							/>
							<Button
								variant="secondary"
								leftIcon={<Replay className="w-5 h-5 -rotate-45" />}
								label="Retry"
								onClick={() => handleRetry(row?.original?.id)}
								loading={isRetrying && currentRetryId === row?.original?.id}
							/>
						</div>
					)
				},
			},
		]

		return base
	}, [
		isRetrying,
		isLoadingInvoice,
		isLoadingReceipt,
		currentDownloadId,
		currentRetryId,
		formatDate,
		formatDateTime,
	])

	const table = useReactTable({
		data,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		state: {
			sorting,
		},
	})

	return { table }
}
